import React, { useState } from "react";
import { Input } from "../ui/input";
import { CONSUMABLE_TYPES, CONSUMABLE_COLORS, Field, SELECT_CLS } from "./constants";
import { CompanySelect } from "./PrinterForm";

export const emptyConsumableForm = (siteId = "", printerIds = []) => ({
    name: "", type: "toner", reference: "", color: "", compatible_printer_ids: printerIds,
    quantity: "0", purchase_date: "", invoice_number: "", min_quantity: "1", unit_price: "", currency: "CHF",
    company_id: "", site_id: siteId, storage_location: "", notes: "",
});

export const consumableToForm = (c) => {
    const f = emptyConsumableForm();
    Object.keys(f).forEach((k) => { if (c[k] !== undefined && c[k] !== null) f[k] = Array.isArray(c[k]) ? [...c[k]] : String(c[k]); });
    f.compatible_printer_ids = [...(c.compatible_printer_ids || [])];
    return f;
};

export const buildConsumablePayload = (f) => ({
    ...f,
    quantity: Number(f.quantity || 0),
    min_quantity: Number(f.min_quantity || 0),
    unit_price: f.unit_price === "" ? null : Number(f.unit_price),
    purchase_date: f.purchase_date || null,
    company_id: f.company_id || null,
    site_id: f.site_id || null,
});

function PrinterChecklist({ printers, selected, onChange }) {
    const [q, setQ] = useState("");
    const list = printers.filter((p) => !q || `${p.name} ${p.brand} ${p.model} ${p.site_name || ""}`.toLowerCase().includes(q.toLowerCase()));
    const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    return (
        <div className="border rounded-md" data-testid="cf-printers">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer les imprimantes..." className="h-8 text-xs border-0 border-b rounded-b-none" />
            <div className="max-h-32 overflow-y-auto p-1.5 space-y-0.5">
                {list.length === 0 && <p className="text-[10px] text-gray-400 px-1 py-2">Aucune imprimante</p>}
                {list.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-xs px-1 py-0.5 rounded hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`cf-printer-${p.id}`} />
                        <span className="truncate">{p.name}</span>
                        <span className="text-[10px] text-gray-400 truncate">{[p.brand, p.model].filter(Boolean).join(" ")}{p.site_name ? ` · ${p.site_name}` : ""}</span>
                    </label>
                ))}
            </div>
            {selected.length > 0 && <p className="text-[10px] text-teal-600 px-2 pb-1">{selected.length} sélectionnée(s)</p>}
        </div>
    );
}

export function ConsumableForm({ form, setForm, printers = [], companies = [], sites = [], isEdit = false }) {
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="consumable-form">
            <Field label="Nom *"><Input value={form.name} onChange={set("name")} placeholder="ex : Toner noir HP 59X" data-testid="cf-name" /></Field>
            <Field label="Type">
                <select value={form.type} onChange={set("type")} className={SELECT_CLS} data-testid="cf-type">
                    {CONSUMABLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
            </Field>
            <Field label="Référence fabricant"><Input value={form.reference} onChange={set("reference")} placeholder="CF259X" className="font-mono" data-testid="cf-reference" /></Field>
            <Field label="Couleur">
                <select value={form.color} onChange={set("color")} className={SELECT_CLS} data-testid="cf-color">
                    {CONSUMABLE_COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
            </Field>
            <Field label="Imprimantes compatibles" className="sm:col-span-2">
                <PrinterChecklist printers={printers} selected={form.compatible_printer_ids} onChange={(ids) => setForm({ ...form, compatible_printer_ids: ids })} />
            </Field>
            {!isEdit && (
                <>
                    <Field label="Stock initial"><Input type="number" min="0" value={form.quantity} onChange={set("quantity")} data-testid="cf-quantity" /></Field>
                    <Field label="Date d'achat (stock initial)"><Input type="date" value={form.purchase_date} onChange={set("purchase_date")} data-testid="cf-purchase-date" /></Field>
                </>
            )}
            <Field label="Seuil d'alerte (stock bas)"><Input type="number" min="0" value={form.min_quantity} onChange={set("min_quantity")} data-testid="cf-min-quantity" /></Field>
            <Field label="Prix unitaire">
                <div className="flex gap-1">
                    <Input type="number" step="0.01" value={form.unit_price} onChange={set("unit_price")} placeholder="0.00" data-testid="cf-unit-price" />
                    <Input value={form.currency} onChange={set("currency")} className="w-16 text-center" />
                </div>
            </Field>
            <Field label="Fournisseur"><CompanySelect value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} companies={companies} testId="cf-company" /></Field>
            <Field label="Site de stockage">
                <select value={form.site_id || ""} onChange={set("site_id")} className={SELECT_CLS} data-testid="cf-site">
                    <option value="">—</option>
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
            </Field>
            <Field label="Emplacement de stockage"><Input value={form.storage_location} onChange={set("storage_location")} placeholder="Armoire IT, étagère 3" data-testid="cf-storage" /></Field>
            {!isEdit && <Field label="N° facture (stock initial)"><Input value={form.invoice_number} onChange={set("invoice_number")} data-testid="cf-invoice" /></Field>}
            <Field label="Notes" className="sm:col-span-2">
                <textarea value={form.notes} onChange={set("notes")} rows={2} className="w-full px-2 py-1.5 text-sm border rounded-md" data-testid="cf-notes" />
            </Field>
        </div>
    );
}
